/**
 * useVoskDictation
 * ================
 * Custom React hook that replaces the browser Web Speech API with a local
 * Vosk WebSocket backend (tools/vosk-server.py).
 *
 * Audio pipeline:
 *   getUserMedia → AudioContext @ 16 kHz → ScriptProcessorNode
 *   → Float32 → Int16 PCM → WebSocket → vosk-server.py → JSON results
 *
 * Usage:
 *   const { isTranscribing, activeDictationField, transcriptionError,
 *           startDictation, stopDictation } = useVoskDictation()
 *
 *   // start dictation for a named field, passing current value + update callback
 *   await startDictation('notes', currentText, (updatedText) => setNotes(updatedText))
 *
 *   // stop (also called automatically on component unmount)
 *   stopDictation()
 */

import { useCallback, useEffect, useRef, useState } from 'react'

const VOSK_WS_URL =
  (typeof process !== 'undefined' && process.env.REACT_APP_VOSK_WS_URL) ||
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/vosk`

export default function useVoskDictation() {
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [activeDictationField, setActiveDictationField] = useState('')
  const [transcriptionError, setTranscriptionError] = useState('')

  const wsRef            = useRef(null)
  const streamRef        = useRef(null)
  const audioContextRef  = useRef(null)
  const processorRef     = useRef(null)
  const sourceRef        = useRef(null)
  const baseTextRef      = useRef('')
  const onUpdateRef      = useRef(null)

  // ── Cleanup helper ─────────────────────────────────────────────────────────
  const stopDictation = useCallback(() => {
    try {
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send('stop')
        }
        wsRef.current.onmessage = null
        wsRef.current.onerror   = null
        wsRef.current.onclose   = null
        wsRef.current.close()
        wsRef.current = null
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect()
        sourceRef.current = null
      }
      if (processorRef.current) {
        processorRef.current.disconnect()
        processorRef.current = null
      }
      if (audioContextRef.current) {
        audioContextRef.current.close()
        audioContextRef.current = null
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop())
        streamRef.current = null
      }
    } catch (_e) {
      // ignore teardown errors
    }
    setIsTranscribing(false)
    setActiveDictationField('')
  }, [])

  // Auto-cleanup on unmount
  useEffect(() => () => stopDictation(), [stopDictation])

  // ── Start dictation ────────────────────────────────────────────────────────
  const startDictation = useCallback(async (fieldName, baseText, onUpdate) => {
    setTranscriptionError('')
    stopDictation()

    baseTextRef.current = String(baseText || '')
    onUpdateRef.current = onUpdate

    // 1. Request microphone
    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1 },
      })
      streamRef.current = stream
    } catch (_err) {
      setTranscriptionError('Microphone access denied. Check browser permissions.')
      return
    }

    // 2. Open WebSocket to Vosk server
    let ws
    try {
      ws = new WebSocket(VOSK_WS_URL)
    } catch (_err) {
      setTranscriptionError(`Cannot reach speech server at ${VOSK_WS_URL}. Ensure vosk-server.py is running.`)
      stream.getTracks().forEach((t) => t.stop())
      streamRef.current = null
      return
    }
    wsRef.current = ws

    ws.onerror = () => {
      setTranscriptionError(
        `Cannot connect to speech server at ${VOSK_WS_URL}. ` +
        'Run:  python tools/vosk-server.py'
      )
      stopDictation()
    }

    ws.onclose = () => {
      setIsTranscribing(false)
      setActiveDictationField('')
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        const text = (data.text || '').trim()
        if (!text) return

        if (data.type === 'partial') {
          // Show interim result without committing base
          const sep  = baseTextRef.current && text ? ' ' : ''
          onUpdateRef.current?.(`${baseTextRef.current}${sep}${text}`)
        } else if (data.type === 'final') {
          // Commit final result as new base
          const sep    = baseTextRef.current && text ? ' ' : ''
          const merged = `${baseTextRef.current}${sep}${text}`
          baseTextRef.current = merged
          onUpdateRef.current?.(merged)
        }
      } catch (_e) {
        // ignore bad JSON
      }
    }

    // 3. Once WebSocket is open, wire up the audio pipeline
    ws.onopen = () => {
      try {
        // Request 16 kHz — browser resamples getUserMedia stream internally
        const audioContext = new AudioContext({ sampleRate: 16000 })
        audioContextRef.current = audioContext

        const source = audioContext.createMediaStreamSource(stream)
        sourceRef.current = source

        // ScriptProcessorNode is deprecated but still works in all desktop
        // browsers. Replace with AudioWorkletNode when production-ready.
        const processor = audioContext.createScriptProcessor(4096, 1, 1)
        processorRef.current = processor

        processor.onaudioprocess = (e) => {
          if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
          const float32 = e.inputBuffer.getChannelData(0)
          const int16   = new Int16Array(float32.length)
          for (let i = 0; i < float32.length; i++) {
            const clamped = Math.max(-1, Math.min(1, float32[i]))
            int16[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff
          }
          wsRef.current.send(int16.buffer)
        }

        source.connect(processor)
        processor.connect(audioContext.destination)

        setIsTranscribing(true)
        setActiveDictationField(fieldName)
      } catch (err) {
        setTranscriptionError(`Audio pipeline error: ${err.message}`)
        stopDictation()
      }
    }
  }, [stopDictation])

  return {
    isTranscribing,
    activeDictationField,
    transcriptionError,
    setTranscriptionError,
    startDictation,
    stopDictation,
  }
}
